import type { ClientTerritorialAuthority } from '@DigitaleDoerfer/digitale-doerfer-api';
import { ClientTerritorialAuthorityTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { LinearGradient } from 'expo-linear-gradient';
import { memo } from 'react';
import { StyleSheet } from 'react-native';

import { AppName } from '@/assets';
import { useGetGeoArea } from '@/features/api/hooks/participant-geo-area/useGetGeoArea';
import { THUMB_HASH } from '@/features/media/constants/image';
import { getSizedMediaItemUrl } from '@/features/media/utils/mediaItem';
import { HomeGeoAreaTitle } from '@/features/start/components/header/HomeGeoAreaTitle';
import { StartPageImage } from '@/features/start/components/header/StartPageImage';
import { Row } from '@/shared/components/layout/Row';
import { Stack } from '@/shared/components/layout/Stack';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const styles = StyleSheet.create({
  headerRow: {
    gap: 24,
    marginHorizontal: 12,
    marginVertical: 24
  },
  appName: { flexShrink: 0 }
});

type Props = {
  homeAreaId?: string;
  territorialAuthority?: ClientTerritorialAuthority;
  onHomeAreaPress: () => void;
};

export const StartScreenHeader = memo(
  ({ homeAreaId, territorialAuthority, onHomeAreaPress }: Props) => {
    const theme = useAppTheme();
    const { data: homeGeoArea } = useGetGeoArea(homeAreaId);

    // Use TAO org's first image if available, otherwise fall back to geo area logo
    const orgImage =
      territorialAuthority?.type ===
      ClientTerritorialAuthorityTypeEnum.Organization
        ? (territorialAuthority.organization?.detailImages?.[0] ??
          territorialAuthority.organization?.overviewImage)
        : undefined;

    const imageUrl = orgImage
      ? getSizedMediaItemUrl(orgImage)?.url
      : getSizedMediaItemUrl(homeGeoArea?.logo)?.url;

    return (
      <Stack>
        <StartPageImage
          source={imageUrl ? { uri: imageUrl } : undefined}
          placeholder={{
            thumbhash:
              orgImage?.thumbHash ?? homeGeoArea?.logo?.thumbHash ?? THUMB_HASH
          }}
        />
        <LinearGradient
          colors={[theme.colors.primary, 'transparent']}
          locations={[0, 0.55]}
          style={[StyleSheet.absoluteFill, { pointerEvents: 'none' }]}
        />
        <Row style={styles.headerRow}>
          <AppName style={styles.appName} />
          <HomeGeoAreaTitle
            name={homeGeoArea?.name}
            homeGeoAreaId={homeGeoArea?.id}
            onPress={onHomeAreaPress}
          />
        </Row>
      </Stack>
    );
  }
);

StartScreenHeader.displayName = 'StartScreenHeader';
